import { useLatest } from 'ahooks';
import { cloneDeep } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { HOTKEY_TYPE, useFramework } from '@/framework';
import { useLoadingState } from '@/framework/core/states/loading-state';
import { getTableClipboardData, pasteToTableLayer } from '@/layers/table-layer/utils/clipboard';
import { registerStyleSheet } from '@/utils/common-kit';
import { Cell, ITableLayer, ITableLayerProps, LayerType, RowOrColSelectedRectInfo } from '@/version-types';
import { CellsSelectedInfo, useTableEditState } from './state';
import { TableAction } from './table-action';
import addOrUpdateLayer from './utils/add-or-update-layer';

export const useActiveTableLayer = () => {
    // --- framework hooks ---
    const framework = useFramework();

    // --- internal states ---
    const [targetTableLayer, setTargetTableLayer] = useState<ITableLayer | null>(null);

    // --- internal refs ---
    const targetLayerIdRef = useLatest(targetTableLayer?.id ?? null);

    useEffect(() => {
        const activeUpdateListenerID = framework.events.onActiveLayerDataUpdate((activeLayer, pickLayer) => {
            const getTargetTableLayer = (): ITableLayer | null => {
                if (!activeLayer) return null;

                let maybeTargetLayer = activeLayer;
                if (activeLayer.layerType === LayerType.GROUP_LAYER && pickLayer) {
                    maybeTargetLayer = pickLayer;
                }

                if (maybeTargetLayer.layerType !== LayerType.TABLE) return null;
                return maybeTargetLayer as ITableLayer;
            };

            setTargetTableLayer(getTargetTableLayer());
        });

        const baseOperateListenerID = framework.events.onBaseOperate((layerId) => {
            if (layerId !== targetLayerIdRef.current) return;

            const layer = framework.getLayer(layerId);
            setTargetTableLayer(layer as ITableLayer);
        });

        return () => {
            framework.events.offEvent(activeUpdateListenerID);
            framework.events.offEvent(baseOperateListenerID);
        };
    }, []);

    return targetTableLayer;
};

export const useTableEditNodeCursorCss = ({
    tableEditNode,
    isActiveTableLayer,
    isDisableMoveState,
}: {
    tableEditNode: HTMLElement | null;
    isActiveTableLayer: boolean;
    isDisableMoveState: boolean;
}) => {
    const lineIsDragging = useTableEditState()((s) => s.lineIsDragging);
    const cssClassName = useState(() => `css-${uuid()}`)[0];

    useEffect(() => {
        if (!tableEditNode) return;
        tableEditNode.classList.add(cssClassName);
        return () => {
            tableEditNode.classList.remove(cssClassName);
        };
    }, [tableEditNode]);

    useEffect(() => {
        if (!isActiveTableLayer) return;
        if (isDisableMoveState) return;
        if (lineIsDragging) return;
        const removeStyleSheet = registerStyleSheet(`
            .${cssClassName} {
                cursor: var(--cursor-move) !important;
            }
        `);
        return removeStyleSheet;
    }, [isActiveTableLayer, isDisableMoveState, lineIsDragging]);
};

export const useTextBizHotKey = () => {
    const framework = useFramework();
    const { getState } = useTableEditState();
    const activeTableLayerRef = useLatest(useActiveTableLayer());

    useEffect(() => {
        const doDelete = (
            layer: ITableLayer,
            selectedInfo: {
                cellsSelectedInfo: CellsSelectedInfo | null;
                rowOrColSelectedRectInfo: RowOrColSelectedRectInfo | null;
            },
        ) => {
            const { cellsSelectedInfo, rowOrColSelectedRectInfo } = selectedInfo;
            const hasSelectedInfo = !!cellsSelectedInfo || !!rowOrColSelectedRectInfo;
            if (!hasSelectedInfo) return;

            let newTableLayer = cloneDeep(layer);
            let cellInfoList: Record<'vIndex' | 'hIndex', number>[] = [];
            if (cellsSelectedInfo) {
                cellInfoList = cellsSelectedInfo.cellInfoList;
            } else if (rowOrColSelectedRectInfo) {
                cellInfoList =
                    rowOrColSelectedRectInfo.direction === 'horizontal'
                        ? (newTableLayer.data.cells[rowOrColSelectedRectInfo.index].filter(Boolean) as Cell[])
                        : (newTableLayer.data.cells
                              .map((row) => row[rowOrColSelectedRectInfo.index])
                              .filter(Boolean) as Cell[]);
            }
            for (const cellInfo of cellInfoList) {
                const cell = newTableLayer.data.cells[cellInfo.vIndex]?.[cellInfo.hIndex];
                if (cell && cell.content.length) {
                    cell.content = '';
                }
            }

            newTableLayer = addOrUpdateLayer({
                frameworkIdentity: framework.identity,
                layer: newTableLayer,
                resetMaterialIds: true,
            });
            framework.operate.setActiveLayer(newTableLayer.id);
            framework.history.write(cloneDeep(layer), newTableLayer);

            new TableAction(framework.identity).emit('delete');
        };

        const copyListenerID = framework.events.on(
            HOTKEY_TYPE.copy,
            () => {
                if (!activeTableLayerRef.current) return false;
                const { cellsSelectedInfo, rowOrColSelectedRectInfo } = getState();
                const hasSelectedInfo = !!cellsSelectedInfo || !!rowOrColSelectedRectInfo;
                if (!hasSelectedInfo) return false;

                getTableClipboardData({
                    frameworkIdentity: framework.identity,
                    layerId: activeTableLayerRef.current.id,
                    selectedInfo: {
                        cellsSelectedInfo,
                        rowOrColSelectedRectInfo,
                    },
                }).then((clipboardData) => {
                    if (!clipboardData) return;
                    navigator.clipboard.write(clipboardData);
                });

                return true;
            },
            9,
        );
        const cutListenerID = framework.events.on(
            HOTKEY_TYPE.cut,
            () => {
                if (!activeTableLayerRef.current) return false;
                const { cellsSelectedInfo, rowOrColSelectedRectInfo } = getState();
                const hasSelectedInfo = !!cellsSelectedInfo || !!rowOrColSelectedRectInfo;
                if (!hasSelectedInfo) return false;

                getTableClipboardData({
                    frameworkIdentity: framework.identity,
                    layerId: activeTableLayerRef.current.id,
                    selectedInfo: {
                        cellsSelectedInfo,
                        rowOrColSelectedRectInfo,
                    },
                }).then((clipboardData) => {
                    if (!clipboardData) return;
                    navigator.clipboard.write(clipboardData);
                });
                doDelete(activeTableLayerRef.current, {
                    cellsSelectedInfo,
                    rowOrColSelectedRectInfo,
                });

                return true;
            },
            9,
        );
        const pasteListenerID = framework.events.on(
            HOTKEY_TYPE.paste,
            () => {
                if (!activeTableLayerRef.current) return false;
                const { cellsSelectedInfo } = getState();
                if (!cellsSelectedInfo?.cellInfoList?.length) return false;

                pasteToTableLayer({
                    frameworkIdentity: framework.identity,
                    layerId: activeTableLayerRef.current.id,
                    cellsSelectedInfo: cellsSelectedInfo,
                });

                new TableAction(framework.identity).emit('paste');

                return true;
            },
            9,
        );
        const selectAllListenerID = framework.events.on(
            HOTKEY_TYPE.selectAll,
            () => {
                if (!activeTableLayerRef.current) return false;
                const { cellsSelectedInfo, rowOrColSelectedRectInfo } = getState();
                const hasSelectedInfo = !!cellsSelectedInfo || !!rowOrColSelectedRectInfo;
                if (!hasSelectedInfo) return false;

                console.log('===hotKey selectAll');
                // return true;
            },
            9,
        );
        const deleteListenerID = framework.events.onDelete((type) => {
            // 有type说明操作来源于shortcut等而不是快捷键, 走默认删除逻辑
            if (type) return false;

            if (!activeTableLayerRef.current) return false;
            const { cellsSelectedInfo, rowOrColSelectedRectInfo } = getState();
            const hasSelectedInfo = !!cellsSelectedInfo || !!rowOrColSelectedRectInfo;
            if (!hasSelectedInfo) return false;

            doDelete(activeTableLayerRef.current, {
                cellsSelectedInfo,
                rowOrColSelectedRectInfo,
            });

            return true;
        }, 9);

        return () => {
            framework.events.off(copyListenerID);
            framework.events.off(cutListenerID);
            framework.events.off(pasteListenerID);
            framework.events.off(selectAllListenerID);
            framework.events.off(deleteListenerID);
        };
    }, []);
};

export const useCellInfosOfLoadingFont = ({
    layerId,
    cells: cellsProp,
    styles,
}: Pick<ITableLayerProps, 'cells' | 'styles'> & { layerId: string }) => {
    const loadingFontFamilyList = useLoadingState()((s) => s.loadingMap[layerId] ?? null) as string[] | null;
    const cellIds = useMemo(() => {
        const cellIds: string[] = [];
        const targetStyleIds = Object.entries(styles)
            .filter(([_, style]) => style.fontFamily && loadingFontFamilyList?.includes(style.fontFamily))
            .map(([id]) => id);
        cellsProp.flat().forEach((cell) => {
            if (cell?.styleIds.some((id) => targetStyleIds.includes(id))) {
                cellIds.push(cell.__renderId);
            }
        });
        return cellIds;
    }, [styles, cellsProp, loadingFontFamilyList]);
    return cellIds;
};
